import { useEffect, useState } from "react";
import TopNav from "../components/TopNav.jsx";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * EnterprisePage · /enterprise
 *
 * Founder ruling 2026-08-22: enterprise-tier price sheet + intake for
 * giants. Every published tier beats the market by at least 20%.
 */
export default function EnterprisePage() {
  const [payload, setPayload] = useState(null);
  const [legalPolicy, setLegalPolicy] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    seat_count_or_scale: "",
    tier_of_interest: "",
    current_provider: "",
    current_annual_spend_usd: "",
    matter_volume_last_year: "",
    role_of_signer: "",
    situation: "",
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/enterprise/deals`).then(r => r.json()).then(setPayload).catch(() => {});
    fetch(`${API}/api/legal-advice/policy`).then(r => r.json()).then(setLegalPolicy).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (form.name.trim().length < 2) { toast.error("Your name is required."); return; }
    if (!form.email.includes("@")) { toast.error("A real email is required."); return; }
    if (form.organization.trim().length < 2) { toast.error("Organization name is required."); return; }
    if (form.seat_count_or_scale.trim().length < 1) { toast.error("Give us your scale (seat count or size)."); return; }
    if (form.situation.trim().length < 40) { toast.error("Describe your ask in a full sentence (40+ characters)."); return; }
    setBusy(true);
    try {
      const body = {
        ...form,
        current_annual_spend_usd: form.current_annual_spend_usd ? parseFloat(form.current_annual_spend_usd) : null,
      };
      const r = await fetch(`${API}/api/enterprise/inquire`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (r.ok) { setSubmitted(d); toast.success("Received. Founder writes enterprise replies himself."); }
      else { toast.error(d?.detail || "Something went wrong."); }
    } catch { toast.error("Network hiccup. Try again in a moment."); }
    finally { setBusy(false); }
  };

  const fmtPrice = (n) => n ? `$${Number(n).toLocaleString()}` : "";

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain" data-testid="enterprise-page">
      <TopNav />
      <div className="mx-auto max-w-6xl px-4 md:px-8 py-12">
        <div className="text-[10px] uppercase tracking-[0.3em] text-sage-accent mb-3">
          FOR THE GIANTS
        </div>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter uppercase mb-4" data-testid="enterprise-headline">
          Enterprise
        </h1>
        <p className="text-lg text-sage-muted leading-relaxed max-w-3xl mb-10 sage-prose">
          Every tier below beats the current market benchmark by at least 20%. Founder ruling 2026-08-22.
          Not a negotiation. Published, honest, and undercut by design.
        </p>

        {/* DEALS GRID */}
        {payload?.deals && (
          <div className="mb-16" data-testid="enterprise-deals-grid">
            <div className="grid md:grid-cols-2 gap-5">
              {payload.deals.map((d) => {
                const marketPrice = d.market_price_usd_per_year || d.market_price_usd_per_user_per_year;
                const sagePrice = d.sage_price_usd_per_year || d.sage_price_usd_per_user_per_year;
                const isPerUser = !!d.market_price_usd_per_user_per_year;
                return (
                  <div
                    key={d.key}
                    data-testid={`enterprise-deal-${d.key}`}
                    className="border-2 border-sage-border p-6 hover:border-sage-accent transition-colors duration-150 bg-sage-pale/10"
                  >
                    <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-1">
                      {d.who}
                    </div>
                    <div className="text-2xl font-bold uppercase tracking-tight text-sage-text mb-4">
                      {d.product}
                    </div>

                    {/* Price comparison */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="border border-sage-border p-3">
                        <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint">Market</div>
                        <div className="text-lg line-through text-sage-muted">{fmtPrice(marketPrice)}</div>
                        <div className="text-[10px] text-sage-faint">{isPerUser ? "per user / year" : "per year"}</div>
                      </div>
                      <div className="border-2 border-sage-accent bg-sage-pale/40 p-3">
                        <div className="text-[10px] uppercase tracking-[0.15em] text-sage-accent">Sage</div>
                        <div className="text-lg font-bold text-sage-text">{fmtPrice(sagePrice)}</div>
                        <div className="text-[10px] text-sage-accent">{isPerUser ? "per user / year" : "per year"} · −{d.sage_discount_pct}%</div>
                      </div>
                    </div>

                    <p className="text-xs italic text-sage-faint mb-3">{d.market_benchmark_note}</p>

                    <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Includes</div>
                    <ul className="text-sm text-sage-muted space-y-1 sage-prose mb-3">
                      {d.includes.map((f, i) => <li key={i}>· {f}</li>)}
                    </ul>
                  </div>
                );
              })}
            </div>
            {payload.custom_deal_note && (
              <div className="mt-6 border border-sage-accent bg-sage-pale/20 p-5" data-testid="enterprise-custom-note">
                <div className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">Bigger than Titan?</div>
                <p className="text-sm text-sage-text sage-prose">{payload.custom_deal_note}</p>
              </div>
            )}
          </div>
        )}

        {/* COFFIN NAILS · placed on every Sage legal product/service page · Founder ruling 2026-08-22 */}
        {legalPolicy?.coffin_nails_on_this_page && (
          <div className="mb-10 border-2 border-sage-text bg-sage-bg p-6" data-testid="enterprise-nails">
            <div className="text-xs uppercase tracking-[0.25em] text-sage-text mb-4">Coffin nails · this door</div>
            <div className="space-y-6">
              {legalPolicy.coffin_nails_on_this_page.map((n) => (
                <div key={n.number} className="border-l-4 border-sage-accent pl-4" data-testid={`enterprise-nail-${n.number}`}>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-1">
                    Nail #{n.number} · {n.author}
                  </div>
                  <p className="text-2xl md:text-3xl text-sage-text sage-prose leading-snug italic mb-2">
                    "{n.kings_line}"
                  </p>
                  <p className="text-xs text-sage-faint italic">{n.signature}</p>
                  <p className="text-xs text-sage-muted italic mt-1">{n.forge_note}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* INQUIRY FORM */}
        <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-sage-text mb-3">
          Talk to us
        </h2>
        <p className="text-sm text-sage-muted sage-prose mb-6">
          Every enterprise inquiry lands directly on the Founder's desk. Expect a real answer within three business days.
        </p>

        {submitted ? (
          <div className="border-2 border-sage-accent bg-sage-pale/30 p-8" data-testid="enterprise-submitted">
            <div className="text-[10px] uppercase tracking-[0.3em] text-sage-accent mb-3">Filed</div>
            <div className="text-xl font-bold uppercase tracking-tight text-sage-text mb-3">
              Received. The Founder writes enterprise replies himself.
            </div>
            <div className="text-sm text-sage-muted sage-prose mb-2">
              Reference: <span className="font-mono text-sage-text">{submitted.inquiry_id}</span>
            </div>
            <div className="text-sm text-sage-muted sage-prose">
              A real reply lands in your inbox within three business days.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Your name</div>
                <input data-testid="enterprise-name" type="text" value={form.name} onChange={update("name")} maxLength={200}
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none" />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Email</div>
                <input data-testid="enterprise-email" type="email" value={form.email} onChange={update("email")} maxLength={200}
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none" />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Organization</div>
                <input data-testid="enterprise-org" type="text" value={form.organization} onChange={update("organization")} maxLength={500}
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none" />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Your role</div>
                <input data-testid="enterprise-role" type="text" value={form.role_of_signer} onChange={update("role_of_signer")} maxLength={200}
                  placeholder="e.g. General Counsel, Head of Ops, CEO"
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none" />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Scale (seat count or size)</div>
                <input data-testid="enterprise-scale" type="text" value={form.seat_count_or_scale} onChange={update("seat_count_or_scale")} maxLength={200}
                  placeholder="e.g. 250 seats · Fortune 500"
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none" />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Tier you're eyeing</div>
                <select data-testid="enterprise-tier" value={form.tier_of_interest} onChange={update("tier_of_interest")}
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none">
                  <option value="">Not sure yet…</option>
                  {(payload?.deals || []).map((d) => (
                    <option key={d.key} value={d.key}>{d.product}</option>
                  ))}
                  <option value="custom">Custom · bigger than Titan</option>
                </select>
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Current provider (optional)</div>
                <input data-testid="enterprise-current-provider" type="text" value={form.current_provider} onChange={update("current_provider")} maxLength={200}
                  placeholder="e.g. Axiom, LegalZoom, in-house GC office"
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none" />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Current annual spend USD (optional)</div>
                <input data-testid="enterprise-current-spend" type="number" min="0" step="1" value={form.current_annual_spend_usd} onChange={update("current_annual_spend_usd")}
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none" />
              </label>
            </div>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">What do you need done · plain english</div>
              <textarea data-testid="enterprise-situation" value={form.situation} onChange={update("situation")}
                maxLength={12000} rows={5}
                placeholder="One paragraph. The problem, the scale, the deadline, and any constraints."
                className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none" />
              <div className="text-[10px] text-sage-faint mt-1">
                {form.situation.trim().length}/40 characters minimum
              </div>
            </label>
            <button data-testid="enterprise-submit" onClick={submit} disabled={busy}
              className="border-2 border-sage-accent bg-sage-accent text-sage-bg px-6 py-3 text-xs uppercase tracking-widest font-bold hover:bg-sage-text hover:border-sage-text disabled:opacity-50 transition-colors">
              {busy ? "Filing…" : "File the enterprise inquiry"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
